import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { get, set } from 'idb-keyval';
import { create } from 'zustand';
import type { CreateOrderRequest } from '@dezza/shared';
import { toast } from '@dezza/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/**
 * Vendas offline: quando não há internet, a venda é guardada no aparelho
 * (IndexedDB) com um clientRequestId único e enviada depois para
 * POST /orders/sync — a API é idempotente, então nunca duplica.
 */

export interface OutboxSale {
  clientRequestId: string;
  companyId: string;
  input: CreateOrderRequest & { clientRequestId: string };
  totalCents: number;
  itemsCount: number;
  createdAt: string;
  lastError?: string;
}

const KEY = 'dz.outbox';

interface OutboxState {
  items: OutboxSale[];
  syncing: boolean;
  load: () => Promise<void>;
}

export const useOutbox = create<OutboxState>((setState) => ({
  items: [],
  syncing: false,
  load: async () => {
    const items = ((await get(KEY)) as OutboxSale[] | undefined) ?? [];
    setState({ items });
  },
}));

async function save(items: OutboxSale[]) {
  await set(KEY, items);
  useOutbox.setState({ items });
}

export async function enqueueOfflineSale(sale: OutboxSale): Promise<void> {
  const items = ((await get(KEY)) as OutboxSale[] | undefined) ?? [];
  await save([...items.filter((i) => i.clientRequestId !== sale.clientRequestId), sale]);
}

export function newClientRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

let inflight: Promise<void> | null = null;

export async function syncOutbox(companyId: string): Promise<{ sent: number; failed: number }> {
  if (inflight) {
    await inflight;
    return { sent: 0, failed: 0 };
  }
  let result = { sent: 0, failed: 0 };
  inflight = (async () => {
    const all = ((await get(KEY)) as OutboxSale[] | undefined) ?? [];
    const mine = all.filter((i) => i.companyId === companyId);
    if (!mine.length || !navigator.onLine) return;
    useOutbox.setState({ syncing: true });
    try {
      const { results } = await api.post<{ results: { clientRequestId: string; ok: boolean; error?: string }[] }>(
        '/orders/sync',
        { orders: mine.map((m) => m.input) },
        { silent: true },
      );
      const okIds = new Set(results.filter((r) => r.ok).map((r) => r.clientRequestId));
      const errors = new Map(results.filter((r) => !r.ok).map((r) => [r.clientRequestId, r.error ?? 'Erro']));
      const remaining = all
        .filter((i) => !okIds.has(i.clientRequestId))
        .map((i) => (errors.has(i.clientRequestId) ? { ...i, lastError: errors.get(i.clientRequestId) } : i));
      await save(remaining);
      result = { sent: okIds.size, failed: errors.size };
    } catch {
      /* continua offline ou servidor indisponível — tenta de novo depois */
    } finally {
      useOutbox.setState({ syncing: false });
    }
  })();
  await inflight;
  inflight = null;
  return result;
}

/** Sincroniza ao abrir o app, ao voltar a internet e a cada minuto. */
export function useOfflineSync(): void {
  const { me } = useAuth();
  const qc = useQueryClient();
  const companyId = me?.company?.id;
  useEffect(() => {
    void useOutbox.getState().load();
  }, []);
  useEffect(() => {
    if (!companyId) return;
    const run = async () => {
      const r = await syncOutbox(companyId);
      if (r.sent) {
        toast.success(`${r.sent} venda(s) feita(s) sem internet foram sincronizadas.`);
        qc.invalidateQueries({ queryKey: ['orders'] });
        qc.invalidateQueries({ queryKey: ['dashboard'] });
        qc.invalidateQueries({ queryKey: ['products'] });
      }
      if (r.failed) toast.error(`${r.failed} venda(s) offline não puderam ser sincronizadas. Veja em Vender › Pendentes.`);
    };
    void run();
    window.addEventListener('online', run);
    const t = setInterval(run, 60_000);
    return () => {
      window.removeEventListener('online', run);
      clearInterval(t);
    };
  }, [companyId, qc]);
}
